import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * DIE BAUSTEINE AUS shadcn/ui — was hier liegt und was ausdrücklich nicht.
 *
 * Die Dateien neben dieser sind der **Quelltext der Bausteine von shadcn/ui**,
 * geholt aus der Registrierung (`new-york-v4`) und auf diese Anwendung
 * übersetzt. Sie sind kein Paket: shadcn liefert Quelltext, den man besitzt,
 * und genau das ist der Grund, warum die Entscheidung gegen eine
 * Komponentenbibliothek — nachzulesen im Kopf von `ui/elements.tsx` — hier
 * nicht gebrochen wird. Es kommt keine Abhängigkeit dazu, die bei jedem
 * Hauptversionswechsel angefasst werden muss; es kommen Dateien dazu, die
 * gelesen und geändert werden können wie jede andere.
 *
 * **Was NICHT hier liegt, und warum.** Genommen ist, was eine
 * Verwendungsstelle hat. Vier Bausteine sind ausdrücklich verworfen:
 *
 * | Baustein | Grund |
 * |---|---|
 * | `select` | Die Prüfläufe fahren `selectOption` und `getByRole('option')` auf 14 Auswahlfeldern. Ein Radix-Auswahlfeld ist ein `<button role="combobox">` mit einer Liste, die es erst beim Öffnen gibt — `selectOption` hat daran nichts zu wählen. Die Felder bleiben `<select>` und bekommen nur die Klassen. |
 * | `dropdown-menu` | `e2e/first-login.spec.ts:70` verlangt `getByRole('button', { name: 'Abmelden' })` SICHTBAR. Ein `DropdownMenuItem` hat die Rolle `menuitem` und existiert vor dem Öffnen nicht — der Punkt fällt in beiden Richtungen durch. |
 * | `dialog` | Radix legt `aria-hidden` auf alles ausserhalb des Portals und `pointer-events: none` auf den `<body>`; ausserdem schliesst es beim Klick daneben, was der Dialog dieser Anwendung ausdrücklich nicht tut (Begründung in `ui/elements.tsx`). Der Aufbau bleibt eigen, das Aussehen kommt aus der Vorlage. |
 * | `tabs`, `tooltip`, `separator` | keine Verwendungsstelle. Ein Baustein ohne Verwendungsstelle ist toter Quelltext. |
 *
 * **Was gegenüber der heutigen Vorlage übersetzt ist.** Sie setzt Tailwind 4
 * voraus, hier läuft 3.4:
 *
 * | Vorlage (Tailwind 4) | hier (3.4) |
 * |---|---|
 * | `shadow-xs` | `shadow-sm` — die Namen sind in 4 um eine Stufe verschoben |
 * | `aria-invalid:…` | entfernt; 3.4 kennt die Kurzform nicht (es wäre `aria-[invalid=true]:`), und nichts in dieser Anwendung setzt `aria-invalid` |
 * | `@container/card-header` | fester Aufbau ohne Behälterabfrage |
 * | `has-data-[slot=card-action]:grid-cols-…` | eine feste zweispaltige Kopfzeile |
 * | `[a&]:hover:…` beim Abzeichen | entfernt; kein Abzeichen dieser Anwendung ist ein Verweis |
 * | `import { Slot } from 'radix-ui'` | entfernt samt `asChild`; keine Verwendungsstelle |
 * | eingebaute `animate-in`-Klassen | Zusatz `tailwindcss-animate` (siehe `tailwind.config.js`) |
 * | `bg-background` beim Umriss-Knopf | `bg-card` — ein Knopf steht in dieser Anwendung auf einer Karte und nicht auf der Seitenfläche |
 *
 * `import * as React` ist zu benannten Einfuhren geworden, und der Alias
 * `@/lib/utils` zu einem gewöhnlichen relativen Pfad: Dieses Repository führt
 * keinen Pfad-Alias, und einen für vier Dateien einzuführen hiesse, jedes
 * Werkzeug davon zu überzeugen — `tsc`, Vite, ESLint.
 */

/**
 * Klassen zusammensetzen, und zwar so, dass die spätere die frühere verdrängt.
 *
 * `clsx` allein hängt nur aneinander: `cn('px-2', 'px-4')` ergäbe `px-2 px-4`,
 * und welche der beiden gewinnt, entschiede die Reihenfolge im erzeugten
 * Stylesheet und nicht die im Aufruf. `twMerge` weiss, dass `px-2` und `px-4`
 * dieselbe Eigenschaft setzen, und wirft die frühere weg. Genau davon lebt
 * jeder Baustein hier: Er bringt seine Grundklassen mit, und die
 * Verwendungsstelle überschreibt einzelne davon über `className`.
 *
 * **`tailwind-merge` ist auf der 2er-Reihe festgenagelt.** Die 3er kennt die
 * Klassennamen von Tailwind 4 — dort heisst der kleinste Schatten `shadow-xs`
 * und `shadow-sm` ist eine Stufe grösser. Gegen Tailwind 3.4 hielte sie die
 * beiden für verschiedene Gruppen und liesse sie nebeneinander stehen.
 */
export function cn(...inputs: readonly ClassValue[]): string {
  return twMerge(clsx(inputs));
}
